import {
  Activity,
  AlertCircle,
  ArrowLeft,
  Building2,
  Calendar,
  Clock,
  CreditCard,
  Edit,
  FileText,
  Pause,
  Play,
  Trash2,
  Users,
} from 'lucide-react'
import type { OrganizationDetail } from '../../hooks/use-organization'

interface OrganizationDetailProps {
  organization: OrganizationDetail
  onBack: () => void
  onSuspend?: () => void
  onReactivate?: () => void
  onEdit?: () => void
  onDelete?: () => void
  loading?: boolean
}

type StatusTone = 'green' | 'amber' | 'red' | 'gray'

export function OrganizationDetail({
  organization: org,
  onBack,
  onSuspend,
  onReactivate,
  onEdit,
  onDelete,
  loading = false,
}: OrganizationDetailProps) {
  const displayStatus = org.account_status === 'suspended' ? 'suspended' : org.subscription_status
  const canShowDeleteAction =
    !!onDelete &&
    ['trial', 'none', 'canceled', 'suspended'].includes(displayStatus) &&
    org.metrics.active_patients === 0 &&
    org.metrics.reports_30d === 0

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getStatusMeta = (status: string): { label: string; tone: StatusTone; description: string } => {
    const meta: Record<string, { label: string; tone: StatusTone; description: string }> = {
      trial: {
        label: 'Trial',
        tone: 'amber',
        description: org.trial_days_remaining !== null
          ? `${org.trial_days_remaining} dia${org.trial_days_remaining === 1 ? '' : 's'} restante${org.trial_days_remaining === 1 ? '' : 's'}`
          : 'Sem cobrança ativa',
      },
      active: {
        label: 'Ativo pago',
        tone: 'green',
        description: org.subscription?.plan_name || 'Assinatura ativa',
      },
      past_due: {
        label: 'Pagamento atrasado',
        tone: 'red',
        description: 'Requer ação de cobrança',
      },
      suspended: {
        label: 'Suspenso',
        tone: 'gray',
        description: org.suspension_reason || 'Acesso interrompido',
      },
      canceled: {
        label: 'Cancelado',
        tone: 'gray',
        description: 'Sem assinatura ativa',
      },
      none: {
        label: 'Sem plano',
        tone: 'gray',
        description: 'Sem assinatura configurada',
      },
    }

    return meta[status] || { label: status, tone: 'gray', description: 'Status não mapeado' }
  }

  const toneClasses: Record<StatusTone, { badge: string; panel: string; icon: string; bar: string }> = {
    green: {
      badge: 'bg-green-50 text-green-700 ring-green-200',
      panel: 'bg-green-50/80 border-green-100',
      icon: 'bg-green-100 text-green-700',
      bar: 'bg-green-500',
    },
    amber: {
      badge: 'bg-amber-50 text-amber-700 ring-amber-200',
      panel: 'bg-amber-50/80 border-amber-100',
      icon: 'bg-amber-100 text-amber-700',
      bar: 'bg-amber-500',
    },
    red: {
      badge: 'bg-red-50 text-red-700 ring-red-200',
      panel: 'bg-red-50/80 border-red-100',
      icon: 'bg-red-100 text-red-700',
      bar: 'bg-red-500',
    },
    gray: {
      badge: 'bg-gray-100 text-gray-700 ring-gray-200',
      panel: 'bg-gray-50 border-gray-200',
      icon: 'bg-gray-100 text-gray-700',
      bar: 'bg-gray-500',
    },
  }

  const statusMeta = getStatusMeta(displayStatus)
  const statusTone = toneClasses[statusMeta.tone]

  const getEngagementColor = (score: number) => {
    if (score >= 70) return 'text-green-600'
    if (score >= 40) return 'text-amber-600'
    return 'text-red-600'
  }

  const getEngagementBarColor = (score: number) => {
    if (score >= 70) return 'bg-green-500'
    if (score >= 40) return 'bg-amber-500'
    return 'bg-red-500'
  }

  const renderInfoRow = (label: string, value: string | null | undefined, mono = false) => (
    <div className="grid grid-cols-1 gap-1 border-b border-gray-100 py-2.5 last:border-0 sm:grid-cols-[minmax(0,0.9fr)_minmax(0,1.2fr)]">
      <span className="text-xs font-medium text-gray-500">{label}</span>
      <span className={`min-w-0 break-words text-sm font-medium text-gray-900 sm:text-right ${mono ? 'font-mono text-xs text-gray-700' : ''}`}>
        {value || '-'}
      </span>
    </div>
  )

  const renderMetricCard = (
    label: string,
    value: string | number,
    Icon: typeof Users,
    colorClass: string,
    valueClass = 'text-gray-900',
    extra?: React.ReactNode
  ) => (
    <div className="min-w-0 rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-3 flex min-w-0 items-center gap-3">
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${colorClass}`}>
          <Icon className="h-4.5 w-4.5" />
        </div>
        <span className="min-w-0 truncate text-sm font-medium text-gray-500">{label}</span>
      </div>
      <div className="flex min-w-0 items-end gap-2">
        <p className={`text-2xl font-bold leading-none ${valueClass}`}>{value}</p>
        {extra}
      </div>
    </div>
  )

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-7 w-48 rounded bg-gray-200" />
        <div className="h-24 rounded-lg bg-gray-100" />
        <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-24 rounded-lg bg-gray-100" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="min-w-0 space-y-4">
      <div className="min-w-0 rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex min-w-0 flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <button
              onClick={onBack}
              className="mt-1 shrink-0 rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
              aria-label="Voltar"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>

            <div className="flex min-w-0 items-start gap-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-teal-100">
                <Building2 className="h-6 w-6 text-teal-600" />
              </div>
              <div className="min-w-0">
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  <h1 className="min-w-0 max-w-full truncate text-xl font-bold leading-tight text-gray-900 md:text-2xl">
                    {org.name}
                  </h1>
                  <span className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${statusTone.badge}`}>
                    {statusMeta.label}
                  </span>
                </div>
                <p className="mt-1 truncate text-sm text-gray-500">{org.slug}</p>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 xl:justify-end">
            <button
              onClick={onEdit}
              className="inline-flex h-9 items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50"
            >
              <Edit className="h-4 w-4" />
              Editar
            </button>
            {org.account_status !== 'suspended' ? (
              <button
                onClick={onSuspend}
                className="inline-flex h-9 items-center gap-2 rounded-lg bg-red-50 px-3 text-sm font-medium text-red-600 transition-colors hover:bg-red-100"
              >
                <Pause className="h-4 w-4" />
                Suspender
              </button>
            ) : (
              <button
                onClick={onReactivate}
                className="inline-flex h-9 items-center gap-2 rounded-lg bg-green-50 px-3 text-sm font-medium text-green-600 transition-colors hover:bg-green-100"
              >
                <Play className="h-4 w-4" />
                Reativar
              </button>
            )}
            {canShowDeleteAction && (
              <button
                onClick={onDelete}
                className="inline-flex h-9 items-center gap-2 rounded-lg border border-red-200 bg-white px-3 text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
                Excluir teste
              </button>
            )}
          </div>
        </div>

        <div className={`mt-4 grid gap-3 rounded-lg border p-3 ${statusTone.panel} lg:grid-cols-[minmax(0,1fr)_auto]`}>
          <div className="flex min-w-0 items-center gap-3">
            <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${statusTone.icon}`}>
              {displayStatus === 'trial' ? <Clock className="h-4.5 w-4.5" /> : <CreditCard className="h-4.5 w-4.5" />}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900">Status comercial: {statusMeta.label}</p>
              <p className="truncate text-xs text-gray-600">{statusMeta.description}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs sm:flex sm:items-center">
            <span className="rounded-md bg-white/70 px-2.5 py-1 font-medium text-gray-700">
              Plano: {org.subscription?.plan_name || 'Trial'}
            </span>
            <span className="rounded-md bg-white/70 px-2.5 py-1 font-medium text-gray-700">
              Próxima: {formatDate(org.subscription?.current_period_end || org.trial_ends_at)}
            </span>
          </div>
        </div>
      </div>

      {(org.alerts.critical > 0 || org.alerts.warning > 0) && (
        <div className="flex flex-wrap gap-2">
          {org.alerts.critical > 0 && (
            <div className="inline-flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
              <AlertCircle className="h-4 w-4" />
              {org.alerts.critical} alerta{org.alerts.critical > 1 ? 's' : ''} crítico{org.alerts.critical > 1 ? 's' : ''}
            </div>
          )}
          {org.alerts.warning > 0 && (
            <div className="inline-flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm font-medium text-amber-700">
              <AlertCircle className="h-4 w-4" />
              {org.alerts.warning} aviso{org.alerts.warning > 1 ? 's' : ''}
            </div>
          )}
        </div>
      )}

      {org.account_status === 'suspended' && org.suspended_at && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <h3 className="text-sm font-semibold text-gray-900">Suspensão</h3>
          <p className="mt-1 text-sm text-gray-600">
            {formatDate(org.suspended_at)}
            {org.suspension_reason ? ` - ${org.suspension_reason}` : ''}
          </p>
        </div>
      )}

      <div className="grid min-w-0 grid-cols-2 gap-3 xl:grid-cols-4">
        {renderMetricCard('Membros ativos', org.metrics.active_members, Users, 'bg-blue-100 text-blue-600')}
        {renderMetricCard('Pacientes ativos', org.metrics.active_patients, Users, 'bg-purple-100 text-purple-600')}
        {renderMetricCard('Relatórios (30d)', org.metrics.reports_30d, FileText, 'bg-teal-100 text-teal-600')}
        {renderMetricCard(
          'Engajamento',
          `${org.metrics.engagement_score}%`,
          Activity,
          'bg-amber-100 text-amber-600',
          getEngagementColor(org.metrics.engagement_score),
          <div className="mb-1 h-1.5 w-16 overflow-hidden rounded-full bg-gray-200">
            <div
              className={`h-full rounded-full ${getEngagementBarColor(org.metrics.engagement_score)}`}
              style={{ width: `${org.metrics.engagement_score}%` }}
            />
          </div>
        )}
      </div>

      <div className="grid min-w-0 grid-cols-1 gap-4 xl:grid-cols-2">
        <div className="min-w-0 rounded-lg border border-gray-200 bg-white p-4">
          <h3 className="mb-2 flex items-center gap-2 text-base font-semibold text-gray-900">
            <CreditCard className="h-4.5 w-4.5 text-gray-500" />
            Cobrança
          </h3>
          <div className="min-w-0">
            {renderInfoRow('Email de cobrança', org.billing_email)}
            {renderInfoRow('Nome de cobrança', org.billing_name)}
            {renderInfoRow('Stripe Customer ID', org.stripe_customer_id, true)}
            {renderInfoRow('Plano', org.subscription?.plan_name || (displayStatus === 'trial' ? 'Trial' : '-'))}
            {renderInfoRow('Ciclo', org.subscription?.billing_interval === 'yearly' ? 'Anual' : 'Mensal')}
            {renderInfoRow('Próxima cobrança', formatDate(org.subscription?.current_period_end || org.trial_ends_at))}
          </div>
        </div>

        <div className="min-w-0 rounded-lg border border-gray-200 bg-white p-4">
          <h3 className="mb-2 flex items-center gap-2 text-base font-semibold text-gray-900">
            <Calendar className="h-4.5 w-4.5 text-gray-500" />
            Datas
          </h3>
          <div className="min-w-0">
            {renderInfoRow('Criado em', formatDate(org.created_at))}
            {renderInfoRow('Última atualização', formatDate(org.updated_at))}
            {renderInfoRow('Última atividade', formatDate(org.last_activity_at))}
            {displayStatus === 'trial' && (
              renderInfoRow(
                'Fim do trial',
                org.trial_days_remaining !== null
                  ? `${org.trial_days_remaining} dia${org.trial_days_remaining === 1 ? '' : 's'} - ${formatDate(org.trial_ends_at)}`
                  : formatDate(org.trial_ends_at)
              )
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
